import React, { useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Button, IconButton, Menu, MenuItem, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { getAnchorProps, MenuAnchor } from '@paragon/client-tools';

export function ResponsiveButtonBar(props: {
  // Screen widths greater than this breakpoint (inclusive) will get buttons instead of a menu.
  breakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl';

  // The icon to use when collapsed into a single menu.
  icon?: React.JSX.Element;

  actions: [string, () => void][];
}) {
  const [menuAnchor, setMenuAnchor] = useState<MenuAnchor>(null);

  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up(props.breakpoint));

  // close the menu if its button is no longer visible
  if (menuAnchor && isLarge) {
    setMenuAnchor(null);
  }

  return (
    <>
      <Stack direction="row" sx={{ alignItems: 'center', display: { xs: 'none', [props.breakpoint]: 'block' } }}>
        {props.actions.map(([text, handler]) => (
          // Use a margin instead of Stack spacing so that buttons wrap nicely when they are next to long titles
          <Button variant="outlined" key={text} onClick={handler} sx={{ mb: 1, mr: 1 }}>
            {text}
          </Button>
        ))}
      </Stack>

      <IconButton
        sx={{ display: { [props.breakpoint]: 'none' } }}
        onClick={(event) => {
          setMenuAnchor({ current: event.currentTarget });
        }}
      >
        {props.icon ?? <MoreVertIcon />}
      </IconButton>

      <Menu
        open={menuAnchor != null}
        {...getAnchorProps(menuAnchor)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setMenuAnchor(null)}
      >
        {props.actions.map(([text, handler]) => (
          <MenuItem
            key={text}
            onClick={() => {
              setMenuAnchor(null);
              handler();
            }}
          >
            {text}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
